import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Search, Plus, X, RefreshCw, Pencil } from "lucide-react";
import useCrmApi from "../../../../components/hook/crm-req";
import useRequest from "../../../../components/hook/use-req";
import { formatCurrency } from "../../../../utils/function";

// ── Types — matches docs/deals-api.md exactly (that guide gives request/
// response field names via its POST/PATCH examples and prose, not a full
// GET schema, so response fields beyond those are read defensively). ──────

type DealStage = "new" | "qualified" | "proposal" | "negotiation" | "follow_up" | "won" | "lost";

interface Deal {
  id: string;
  contact_id?: string | null;
  lead_id?: string | null;
  title: string;
  product_or_service?: string | null;
  amount?: string | number | null;
  currency?: string | null;
  stage: DealStage;
  assigned_to_user_id?: string | null;
  description?: string | null;
  expected_close_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface TeamMember {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

const STAGES: DealStage[] = ["new", "qualified", "proposal", "negotiation", "follow_up", "won", "lost"];

const STAGE_STYLES: Record<DealStage, string> = {
  new: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  qualified: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  proposal: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400",
  negotiation: "bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400",
  follow_up: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  won: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  lost: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
};

const stageLabel = (s: DealStage) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const toDateTimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16);
};

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

interface DealFormState {
  title: string;
  product_or_service: string;
  amount: string;
  currency: string;
  assigned_to_user_id: string;
  description: string;
  expected_close_at: string;
}

const EMPTY_FORM: DealFormState = {
  title: "",
  product_or_service: "",
  amount: "",
  currency: "INR",
  assigned_to_user_id: "",
  description: "",
  expected_close_at: "",
};

const toForm = (deal: Deal): DealFormState => ({
  title: deal.title || "",
  product_or_service: deal.product_or_service || "",
  amount: deal.amount != null ? String(deal.amount) : "",
  currency: deal.currency || "INR",
  assigned_to_user_id: deal.assigned_to_user_id || "",
  description: deal.description || "",
  expected_close_at: toDateTimeLocal(deal.expected_close_at),
});

// Create (POST /deals) and edit (PATCH /deals/{id}) share the same fields —
// the API doc's two examples list an overlapping-but-not-identical field
// set; this form covers the union of both since all of them are genuine
// deal properties either way.
const DealFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  deal: Deal | null;
  team: TeamMember[];
  contactId?: string;
  onSaved: () => void;
}> = ({ isOpen, onClose, deal, team, contactId, onSaved }) => {
  const token = localStorage.getItem("authToken");
  const [form, setForm] = useState<DealFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: createDeal } = useCrmApi("/deals", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: updateDeal } = useCrmApi("/deals", "PATCH", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    setForm(deal ? toForm(deal) : EMPTY_FORM);
  }, [deal, isOpen]);

  const update = <K extends keyof DealFormState>(key: K, value: DealFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Give this deal a title.");
      return;
    }
    setSubmitting(true);
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      product_or_service: form.product_or_service || null,
      amount: form.amount.trim() === "" ? null : form.amount.trim(),
      currency: form.currency || null,
      assigned_to_user_id: form.assigned_to_user_id || null,
      description: form.description || null,
      expected_close_at: form.expected_close_at ? new Date(form.expected_close_at).toISOString() : null,
    };

    let response: any;
    let status: number;
    if (deal) {
      [response, status] = await updateDeal(payload, undefined, deal.id);
    } else {
      [response, status] = await createDeal({ ...payload, contact_id: contactId });
    }
    setSubmitting(false);

    if (status >= 200 && status < 300) {
      toast.success(deal ? "Deal updated." : "Deal created.");
      onSaved();
      onClose();
    } else {
      toast.error(response?.detail || response?.message || `Failed to ${deal ? "update" : "create"} deal.`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{deal ? "Edit Deal" : "New Deal"}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Annual facility management plan" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Product / Service</label>
            <input value={form.product_or_service} onChange={(e) => update("product_or_service", e.target.value)} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Amount</label>
              <input type="number" value={form.amount} onChange={(e) => update("amount", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Currency</label>
              <input value={form.currency} onChange={(e) => update("currency", e.target.value.toUpperCase())} maxLength={3} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Assigned To</label>
            <select value={form.assigned_to_user_id} onChange={(e) => update("assigned_to_user_id", e.target.value)} className={inputClass}>
              <option value="">Unassigned</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {`${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || m.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Expected Close</label>
            <input
              type="datetime-local"
              value={form.expected_close_at}
              onChange={(e) => update("expected_close_at", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={3} className={`${inputClass} resize-none`} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg shadow-sm"
          >
            {submitting ? "Saving…" : deal ? "Save Changes" : "Create Deal"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick stage changer — PATCH /deals/{id}/stage. Marking "won" optionally
// attaches transaction details; per the doc, omitting them still creates an
// internal completed transaction, so the extra fields are optional here too.
const StageMenu: React.FC<{ deal: Deal; onChanged: (stage: DealStage) => void }> = ({ deal, onChanged }) => {
  const token = localStorage.getItem("authToken");
  const [open, setOpen] = useState(false);
  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [txId, setTxId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // The dropdown is portaled to <body> and positioned ABOVE the trigger —
  // the deals table sits inside an `overflow-x-auto` wrapper (which also
  // clips vertically), so an in-flow absolute menu was getting cut off.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ bottom: number; left: number } | null>(null);

  const { makeRequest: updateStage } = useCrmApi("/deals", "PATCH", { Authorization: `Bearer ${token}` });

  const updateCoords = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => updateCoords();
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const submitStage = async (stage: DealStage, transaction?: Record<string, unknown>) => {
    setSubmitting(true);
    const payload: Record<string, unknown> = { stage };
    if (transaction) payload.transaction = transaction;
    const [response, status] = await updateStage(payload, undefined, `${deal.id}/stage`);
    setSubmitting(false);
    if (status >= 200 && status < 300) {
      toast.success(`Deal marked ${stageLabel(stage)}.`);
      onChanged(stage);
    } else {
      toast.error(response?.detail || response?.message || "Failed to update stage.");
    }
  };

  const handlePick = (stage: DealStage) => {
    setOpen(false);
    if (stage === deal.stage) return;
    if (stage === "won") {
      setWonModalOpen(true);
      return;
    }
    submitStage(stage);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((s) => !s);
        }}
        disabled={submitting}
        className={`text-xs font-medium px-2.5 py-1 rounded-full disabled:opacity-60 ${STAGE_STYLES[deal.stage] || STAGE_STYLES.new}`}
      >
        {stageLabel(deal.stage)}
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            style={{ position: "fixed", bottom: coords.bottom, left: coords.left }}
            className="z-[80] w-36 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden"
          >
            {STAGES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handlePick(s)}
                className={`flex w-full items-center px-3 py-1.5 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  s === deal.stage ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"
                }`}
              >
                {stageLabel(s)}
              </button>
            ))}
          </div>,
          document.body
        )}

      {wonModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setWonModalOpen(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Mark deal as won</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Optional — link an external transaction/order. Leave blank and an internal completed transaction is created automatically.
            </p>
            <div className="space-y-2.5">
              <input
                value={txId}
                onChange={(e) => setTxId(e.target.value)}
                placeholder="External transaction ID (optional)"
                className={inputClass}
              />
              <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID (optional)" className={inputClass} />
            </div>
            <div className="flex items-center justify-end gap-2.5 mt-4">
              <button
                type="button"
                onClick={() => setWonModalOpen(false)}
                className="px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={async () => {
                  const transaction =
                    txId.trim() || orderId.trim()
                      ? {
                          ...(txId.trim() ? { external_transaction_id: txId.trim() } : {}),
                          ...(orderId.trim() ? { order_id: orderId.trim() } : {}),
                          status: "completed",
                        }
                      : undefined;
                  await submitStage("won", transaction);
                  setWonModalOpen(false);
                  setTxId("");
                  setOrderId("");
                }}
                className="px-3.5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 rounded-lg"
              >
                {submitting ? "Saving…" : "Mark Won"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── DealsTab ─────────────────────────────────────────────────────────────────
// GET /deals?contact_id={id} — this customer's sales opportunities. Fetches
// every page up front (same approach as the Feedback tab) rather than
// reading the X-Total-Count header, since the shared useCrmApi hook doesn't
// expose response headers.
const PAGE_SIZE = 100;
const MAX_PAGES = 25;

const DealsTab: React.FC<{ contactId?: string }> = ({ contactId }) => {
  const token = localStorage.getItem("authToken");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<"" | DealStage>("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);

  const { makeRequest: getDeals } = useCrmApi("/deals", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTeamList } = useRequest("/profile/users", "GET", { Authorization: `Bearer ${token}` });

  const fetchDeals = async () => {
    if (!contactId) return;
    setLoading(true);
    setError(null);
    try {
      let offset = 0;
      let all: Deal[] = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const [res, status] = await getDeals(undefined, { contact_id: contactId, limit: PAGE_SIZE, offset });
        if (status < 200 || status >= 300) throw new Error(res?.detail || res?.message || "Failed to load deals.");
        const items: Deal[] = Array.isArray(res) ? res : Array.isArray(res?.results) ? res.results : Array.isArray(res?.data) ? res.data : [];
        all = all.concat(items);
        offset += PAGE_SIZE;
        if (items.length < PAGE_SIZE) break;
      }
      setDeals(all);
    } catch (err: any) {
      setError(err?.message || "Failed to load deals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    (async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) setTeam(response);
        else if (Array.isArray(response?.users)) setTeam(response.users);
        else if (Array.isArray(response?.results)) setTeam(response.results);
      } catch {
        setTeam([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const teamNameById = useMemo(() => {
    const map: Record<string, string> = {};
    team.forEach((m) => {
      map[m.id] = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || m.id;
    });
    return map;
  }, [team]);

  const filtered = useMemo(() => {
    let list = deals;
    if (stageFilter) list = list.filter((d) => d.stage === stageFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          (d.product_or_service || "").toLowerCase().includes(q) ||
          (d.description || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  }, [deals, search, stageFilter]);

  const handleStageChanged = (dealId: string, stage: DealStage) => {
    setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage } : d)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{error}</p>
        <button type="button" onClick={fetchDeals} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => {
            setEditingDeal(null);
            setIsFormOpen(true);
          }}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg transition-colors"
        >
          <Plus size={14} /> New Deal
        </button>

        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search Deals"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30"
          />
        </div>

        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as DealStage | "")}
          className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30"
        >
          <option value="">All Stages</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {stageLabel(s)}
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
          {deals.length === 0 ? "No deals for this customer yet." : "No deals match your search."}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Deal</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide hidden md:table-cell">Assigned To</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide hidden lg:table-cell">Expected Close</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Created</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate max-w-[220px]">{d.title}</p>
                      {d.product_or_service && <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[220px]">{d.product_or_service}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                      {d.amount != null && d.currency ? formatCurrency(Number(d.amount), d.currency, "symbol") : "—"}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <StageMenu deal={d} onChanged={(stage) => handleStageChanged(d.id, stage)} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                      {d.assigned_to_user_id ? teamNameById[d.assigned_to_user_id] || d.assigned_to_user_id : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell whitespace-nowrap">
                      {formatDate(d.expected_close_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{formatDate(d.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingDeal(d);
                          setIsFormOpen(true);
                        }}
                        title="Edit deal"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DealFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        deal={editingDeal}
        team={team}
        contactId={contactId}
        onSaved={fetchDeals}
      />
    </div>
  );
};

export default DealsTab;
